import React, { useState, useEffect, useCallback } from 'react';
import './styles/global.css';
import { LoginPage }     from './pages/Login';
import { ResetPasswordPage } from './pages/ResetPassword';
import { Dashboard }     from './pages/Dashboard';
import {
  InvestorsPage, InvestorProfilePage, LegacyPoolPage, ReturnsPage, WithdrawalsPage,
  InstitutionsPage, InstProfilePage,
  DealsPage, DealDetailPage as CapitalDealDetailPage, AllocationPage,
  ReportsPage, NotificationsPage, AuditPage, SettingsPage, XeroJournalPage,
} from './pages/AllPages';
import {
  DealDashboard,
  DealPipeline,
  DealDetail,
  IcQueuePage,
  TlQcQueuePage,
  FinanceQueuePage,
  ProjectsPage,
  RepaymentQueuePage,
  ReconciliationQueuePage,
  CapitalQueuePage,
  DocumentsPage,
  TasksPage,
  EmailsPage,
  TemplatesPage,
  KpiDashboard,
  DealRegistersPage,
} from './pages/ops';
import NewDealWizardModal from './features/deals/NewDealWizardModal';
import { Sidebar, Topbar } from './components/layout';
import { auth as authApi, setToken, getToken } from './services/api';

// ─── Page registry ────────────────────────────────────────────────────────────
const pageMap = {
  dashboard:     Dashboard,
  investors:     InvestorsPage,
  deposits:      InvestorsPage,
  'inv-profile': InvestorProfilePage,
  'legacy-pool': LegacyPoolPage,
  returns:       ReturnsPage,
  withdrawals:   WithdrawalsPage,
  'xero-journal':XeroJournalPage,
  institutions:  InstitutionsPage,
  'inst-profile':InstProfilePage,
  deals:         DealsPage,
  'deal-detail': CapitalDealDetailPage,
  allocation:    AllocationPage,
  reports:       ReportsPage,
  notifications: NotificationsPage,
  audit:         AuditPage,
  settings:      SettingsPage,

  // Operations
  'dm-dashboard':      DealDashboard,
  'dm-pipeline':       DealPipeline,
  'dm-deal-detail':    DealDetail,
  'dm-ic':             IcQueuePage,
  'dm-tl-qc':          TlQcQueuePage,
  'dm-capital':        CapitalQueuePage,
  'dm-finance':        FinanceQueuePage,
  'dm-projects':       ProjectsPage,
  'dm-repayment':      RepaymentQueuePage,
  'dm-reconciliation': ReconciliationQueuePage,
  'dm-registers':      DealRegistersPage,
  'dm-templates':      TemplatesPage,
  'dm-tasks':          TasksPage,
  'dm-documents':      DocumentsPage,
  'dm-emails':         EmailsPage,
  'kpi-dashboard':     KpiDashboard,
  'admin-registers':   DealRegistersPage,
};

// ─── Loading spinner ─────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--brand-dark)', zIndex: 300,
    }}>
      <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
        <div style={{
          width: 36, height: 36, border: '3px solid rgba(255,255,255,0.2)',
          borderTop: '3px solid var(--accent)', borderRadius: '50%',
          animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
        }} />
        <div style={{ fontSize: 13 }}>Loading…</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

// ─── Toast notification ───────────────────────────────────────────────────────
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const bg = type === 'error' ? 'var(--danger-bg)' : type === 'warn' ? 'var(--warning-bg)' : 'var(--success-bg)';
  const color = type === 'error' ? 'var(--danger)' : type === 'warn' ? 'var(--warning)' : 'var(--success)';

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 500,
      background: bg, border: `1px solid ${color}`, color,
      padding: '12px 16px', borderRadius: 12, fontSize: 13, fontWeight: 600,
      display: 'flex', alignItems: 'center', gap: 10, maxWidth: 340,
      boxShadow: '0 12px 32px rgba(20, 36, 31, 0.12)',
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color, fontSize: 15 }}>✕</button>
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
function App() {
  const [authState, setAuthState]   = useState({ loading: true, user: null });
  const [activePage, setActivePage] = useState('dashboard');
  const [toast, setToast]           = useState(null);
  const [selectedIds, setSelectedIds] = useState({});
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [newDealOpen, setNewDealOpen] = useState(false);
  const [resetToken, setResetToken] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('reset_token');
  });

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setAuthState({ loading: false, user: null });
      return;
    }
    authApi.me()
      .then(res => setAuthState({ loading: false, user: res.user }))
      .catch(() => {
        setToken(null);
        setAuthState({ loading: false, user: null });
      });
  }, []);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (!authState.user) return;
    const p = new URLSearchParams(window.location.search);
    if (p.get('xero_connected')) {
      setActivePage('xero-journal');
      showToast('Xero connected successfully — refresh token saved to database');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (p.get('xero_error')) {
      setActivePage('xero-journal');
      showToast(`Xero connection failed: ${decodeURIComponent(p.get('xero_error'))}`, 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [authState.user, showToast]);

  const handleLogin = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    setToken(res.token);
    setAuthState({ loading: false, user: res.user });
    showToast(`Welcome back, ${res.user.fullName.split(' ')[0]}!`);
  }, [showToast]);

  const handleLogout = useCallback(async () => {
    try { await authApi.logout(); } catch (_) {}
    setToken(null);
    setAuthState({ loading: false, user: null });
    setActivePage('dashboard');
  }, []);

  const navigate = useCallback((page, ids = {}) => {
    if (page === 'dm-new-deal') {
      setNewDealOpen(true);
      setMobileNavOpen(false);
      return;
    }
    setActivePage(page);
    setMobileNavOpen(false);
    if (Object.keys(ids).length) {
      setSelectedIds(prev => ({ ...prev, ...ids }));
    }
    const content = document.getElementById('main-content');
    if (content) content.scrollTop = 0;
  }, []);

  if (authState.loading) return <Spinner />;

  if (!authState.user) {
    if (resetToken) {
      return (
        <>
          <ResetPasswordPage
            token={resetToken}
            onDone={() => {
              setResetToken(null);
              window.history.replaceState({}, '', window.location.pathname);
            }}
          />
          {toast && <Toast {...toast} onClose={() => setToast(null)} />}
        </>
      );
    }

    return (
      <>
        <LoginPage onLogin={handleLogin} />
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      </>
    );
  }

  const PageComponent = pageMap[activePage] || Dashboard;

  return (
    <div className="app-layout">
      <Sidebar
        activePage={activePage}
        onNavigate={navigate}
        onSignOut={handleLogout}
        user={authState.user}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className="main-area">
        <Topbar
          activePage={activePage}
          onNavigate={navigate}
          user={authState.user}
          onMenuToggle={() => setMobileNavOpen(o => !o)}
        />
        <div className="page-content" id="main-content">
          <PageComponent
            key={activePage}
            onNavigate={navigate}
            user={authState.user}
            selectedIds={selectedIds}
            showToast={showToast}
            activePage={activePage}
          />
        </div>
      </div>
      <NewDealWizardModal
        open={newDealOpen}
        onClose={() => setNewDealOpen(false)}
        onNavigate={navigate}
        showToast={showToast}
      />
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

export default App;
